"use client";

import { useCallback, useEffect, useMemo } from "react";
import {
  Background,
  BackgroundVariant,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  defaultEdges,
  INFINITETALK_INPUT_PORTS,
  inferTargetInput,
  isEdgeAllowed,
  validateGraph,
  type GraphEdge,
} from "@/lib/workflow-graph";

/**
 * 可编辑工作流画布：
 * - 模块从下方模块库拖入画布（HTML5 DnD），节点可拖动、可移除（✕）
 * - 连线可拖拽重连，非法连接实时拦截（规则见 lib/workflow-graph）
 * - 节点位置与连线一起按模板持久化，管线真的按这张图执行
 */

export const DND_MIME = "application/easyshot-module";

export interface CanvasNodeSpec {
  key: string;
  x: number;
  y: number;
}

export interface CanvasTemplate {
  id: string;
  name: string;
  tone: string;
  provider: { type: string; engine?: string; endpoint?: string };
  avatarIds: string[];
  voiceIds: string[];
  canvas?: { nodes: CanvasNodeSpec[]; edges?: GraphEdge[] };
  moduleConfig?: Record<
    string,
    {
      inputs?: string[];
      outputs?: string[];
      keyColor?: string;
      deliverables?: string[];
      promptText?: string;
      negativePrompt?: string;
      referencePolicy?: string;
      videoProvider?: string;
      videoEngine?: string;
      generationMode?: string;
      imageProvider?: string;
      imageEngine?: string;
      imageAspectRatio?: string;
      llmProvider?: string;
      llmModel?: string;
      llmPurpose?: string;
      keywordCount?: number;
      keywordLanguage?: string;
      matchScriptOrder?: boolean;
      materialProviders?: string[];
      materialMode?: string;
      materialFallback?: string;
      clipDurationSec?: number;
      stockLicenseRequired?: boolean;
      ttsProvider?: string;
      ttsVoiceId?: string;
      ttsSpeed?: number;
      ttsVolume?: number;
      subtitleEnabled?: boolean;
      subtitleMode?: string;
      subtitleFont?: string;
      subtitleSize?: number;
      subtitleColor?: string;
      subtitleHighlightColor?: string;
      subtitlePosition?: string;
      bgmEnabled?: boolean;
      bgmSource?: string;
      bgmTrackId?: string;
      bgmVolumeDb?: number;
      voiceDuckingDb?: number;
      width?: number;
      height?: number;
      fps?: number;
      transition?: string;
      transitionDurationMs?: number;
      publishEnabled?: boolean;
      publishPlatforms?: string[];
      mediaLabel?: string;
      mediaSource?: string;
      mediaKinds?: string[];
      mediaMaxItems?: number;
      mediaRequired?: boolean;
      valueName?: string;
      valueType?: string;
      valueDefault?: string;
      valueDescription?: string;
      valueRequired?: boolean;
    }
  >;
  enabled: boolean;
}

export interface CanvasStats {
  products: number;
  avatarsLive: number;
  avatarsTotal: number;
  voicesLive: number;
  voicesTotal: number;
  queued: number;
  active: number;
  succeeded: number;
  failed: number;
}

export const MODULE_META: Record<
  string,
  { icon: string; title: string; configurable: boolean }
> = {
  topic: { icon: "🎯", title: "Topic / Subject Input", configurable: true },
  media: { icon: "🖼️", title: "Media input", configurable: true },
  value: { icon: "🔣", title: "Value input", configurable: true },
  product: { icon: "📦", title: "Product Digital Twin", configurable: true },
  avatar: { icon: "🧍", title: "Avatar container", configurable: true },
  voice: { icon: "🎙️", title: "Voice container", configurable: true },
  script: { icon: "✍️", title: "Script engine", configurable: true },
  llm: { icon: "🧠", title: "LLM", configurable: true },
  keywords: { icon: "🔎", title: "Search keywords", configurable: true },
  materials: { icon: "🎞️", title: "Material sources", configurable: true },
  tts: { icon: "🗣️", title: "Text to speech", configurable: true },
  subtitles: { icon: "💬", title: "Subtitles", configurable: true },
  bgm: { icon: "🎵", title: "Background music", configurable: true },
  prompt: { icon: "💬", title: "Prompt", configurable: true },
  image_compose: {
    icon: "🪄",
    title: "Image composer",
    configurable: true,
  },
  aigc: { icon: "✨", title: "Video engine", configurable: true },
  infinitetalk: { icon: "♾️", title: "InfiniteTalk", configurable: true },
  compose: { icon: "✂️", title: "FFmpeg composer", configurable: true },
  render: { icon: "🎬", title: "Render provider", configurable: true },
  chroma: {
    icon: "🟩",
    title: "Cutout & background composer",
    configurable: true,
  },
  output: { icon: "🗂️", title: "Output · R2", configurable: false },
  publish: { icon: "🚀", title: "Publish", configurable: true },
};

export const DEFAULT_LAYOUT: CanvasNodeSpec[] = [
  { key: "product", x: 0, y: 0 },
  { key: "avatar", x: 0, y: 170 },
  { key: "voice", x: 0, y: 340 },
  { key: "script", x: 320, y: 0 },
  { key: "render", x: 640, y: 170 },
  // Generic templates do not include post-processing unless explicitly added.
  { key: "output", x: 960, y: 170 },
];

interface NodeData extends Record<string, unknown> {
  moduleKey: string;
  subtitle: string;
  badge?: string;
  badgeTone?: string;
  onConfigure?: (key: string) => void;
  onRemove?: (key: string) => void;
}

const BADGE_STYLE: Record<string, string> = {
  accent: "bg-[var(--accent)]/18 text-[var(--accent-2)]",
  success: "bg-emerald-500/15 text-emerald-300",
  warning: "bg-amber-500/15 text-amber-300",
  danger: "bg-red-500/15 text-red-300",
  neutral: "bg-white/10 text-white/60",
};

function CanvasNode({ data }: NodeProps<Node<NodeData>>) {
  const meta = MODULE_META[data.moduleKey];
  const hasNamedInputs = data.moduleKey === "infinitetalk";
  return (
    <div
      className={`group rounded-2xl border border-[var(--border-strong)] bg-[#14141f] p-4 shadow-xl ${hasNamedInputs ? "w-64" : "w-56"}`}
    >
      {!hasNamedInputs && (
        <Handle
          type="target"
          position={Position.Left}
          className="!size-2 !border-0 !bg-[var(--accent)]"
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="grid size-9 place-items-center rounded-xl bg-white/8 text-base">
          {meta.icon}
        </div>
        <div className="flex items-center gap-1.5">
          {data.badge && (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE_STYLE[data.badgeTone ?? "neutral"]}`}
            >
              {data.badge}
            </span>
          )}
          {meta.configurable && (
            <button
              type="button"
              title="Configure module"
              className="nodrag grid size-6 place-items-center rounded-lg bg-white/8 text-[11px] text-white/60 transition hover:bg-white/15 hover:text-white"
              onClick={(e) => {
                e.stopPropagation();
                data.onConfigure?.(data.moduleKey);
              }}
            >
              ⚙
            </button>
          )}
          <button
            type="button"
            title="Remove from canvas"
            className="nodrag grid size-6 place-items-center rounded-lg bg-white/8 text-[11px] text-white/40 opacity-0 transition group-hover:opacity-100 hover:bg-white/15 hover:text-[var(--danger)]"
            onClick={(e) => {
              e.stopPropagation();
              data.onRemove?.(data.moduleKey);
            }}
          >
            ✕
          </button>
        </div>
      </div>
      <div className="mt-2.5 text-[13px] font-semibold text-white">
        {meta.title}
      </div>
      <div className="mt-1 text-[11px] leading-4 text-white/45">
        {data.subtitle}
      </div>
      {hasNamedInputs && (
        <div className="mt-3 space-y-1.5 border-t border-white/8 pt-3">
          {INFINITETALK_INPUT_PORTS.map((port) => (
            <div
              key={port.id}
              className="relative flex h-6 items-center rounded-md bg-white/[0.035] pl-2 text-[10px] text-white/55"
            >
              <Handle
                id={port.id}
                type="target"
                position={Position.Left}
                className="!-left-4 !top-1/2 !size-2 !border-0 !bg-[var(--accent)]"
              />
              {port.label}
            </div>
          ))}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Right}
        className="!size-2 !border-0 !bg-[var(--accent)]"
      />
    </div>
  );
}

const nodeTypes = { canvas: CanvasNode };

function describeModule(
  key: string,
  template: CanvasTemplate,
  stats: CanvasStats,
): { subtitle: string; badge?: string; badgeTone?: string } {
  const cfg = template.moduleConfig?.[key];
  switch (key) {
    case "topic":
      return {
        subtitle: cfg?.inputs?.length
          ? `Accepts ${cfg.inputs.join(" · ")}`
          : "Topic, keywords, creative brief or reference URL",
        badge: "input",
        badgeTone: "accent",
      };
    case "media":
      return {
        subtitle: `${cfg?.mediaLabel || "Media"} · ${(cfg?.mediaKinds ?? ["image", "video"]).join(" + ")} · up to ${cfg?.mediaMaxItems ?? 4}`,
        badge: cfg?.mediaSource ?? "customer upload",
        badgeTone: "accent",
      };
    case "value":
      return {
        subtitle: `${cfg?.valueName || "value"}${cfg?.valueDefault ? ` = ${cfg.valueDefault.slice(0, 42)}${cfg.valueDefault.length > 42 ? "…" : ""}` : ""}`,
        badge: cfg?.valueType ?? "text",
        badgeTone: "accent",
      };
    case "product":
      return {
        subtitle: cfg?.inputs?.length
          ? `in: ${cfg.inputs.join(" + ")} · out: ${cfg.outputs?.length ?? 0} element(s)`
          : "Structured product assets from customer material",
        badge: `${stats.products} captured`,
        badgeTone: "neutral",
      };
    case "avatar":
      return {
        subtitle: template.avatarIds.length
          ? `Pool limited to ${template.avatarIds.length} avatar(s)`
          : "Full roster available to this template",
        badge: `${stats.avatarsLive}/${stats.avatarsTotal} live`,
        badgeTone: "neutral",
      };
    case "voice":
      return {
        subtitle: template.voiceIds.length
          ? `Pool limited to ${template.voiceIds.length} voice(s)`
          : "Full voice list available to this template",
        badge: `${stats.voicesLive}/${stats.voicesTotal} live`,
        badgeTone: "neutral",
      };
    case "script":
      return {
        subtitle: `Script rules · tone: ${template.tone.slice(0, 60)}${template.tone.length > 60 ? "…" : ""}`,
        badge: "rules",
        badgeTone: "accent",
      };
    case "llm":
      return {
        subtitle: cfg?.llmModel
          ? `${cfg.llmProvider ?? "LLM"} · ${cfg.llmModel}`
          : "Select the language model used by this workflow",
        badge: cfg?.llmProvider ?? "LLM",
        badgeTone: "accent",
      };
    case "keywords":
      return {
        subtitle: `${cfg?.keywordCount ?? 3} per scene · ${cfg?.keywordLanguage ?? "en"} · ${cfg?.matchScriptOrder === false ? "free order" : "script order"}`,
        badge: "LLM",
        badgeTone: "accent",
      };
    case "materials":
      return {
        subtitle: (cfg?.materialProviders ?? ["product_uploads", "product_page", "pexels", "pixabay", "coverr", "seedance"]).join(" → "),
        badge: `${cfg?.materialMode ?? "hybrid"} · ${cfg?.clipDurationSec ?? 3}s`,
        badgeTone: "accent",
      };
    case "tts":
      return {
        subtitle: `${cfg?.ttsProvider ?? "edge"} · ${cfg?.ttsVoiceId || "selected EasyShot voice"} · ${cfg?.ttsSpeed ?? 1}x`,
        badge: "voice",
        badgeTone: "accent",
      };
    case "subtitles":
      return {
        subtitle: cfg?.subtitleEnabled === false
          ? "Disabled"
          : `${cfg?.subtitleMode ?? "sentence"} · ${cfg?.subtitlePosition ?? "bottom"} · ${cfg?.subtitleFont ?? "Inter"}`,
        badge: cfg?.subtitleEnabled === false ? "off" : "ASS/SRT",
        badgeTone: cfg?.subtitleEnabled === false ? "neutral" : "accent",
      };
    case "bgm":
      return {
        subtitle: cfg?.bgmEnabled === false
          ? "Disabled"
          : `${cfg?.bgmSource ?? "library"} · ${cfg?.bgmVolumeDb ?? -22} dB · duck ${cfg?.voiceDuckingDb ?? -6} dB`,
        badge: cfg?.bgmEnabled === false ? "off" : "mix",
        badgeTone: "neutral",
      };
    case "compose":
      return {
        subtitle: `${cfg?.width ?? 1080}×${cfg?.height ?? 1920} · ${cfg?.fps ?? 30} fps · ${cfg?.transition ?? "fade"}`,
        badge: "FFmpeg",
        badgeTone: "warning",
      };
    case "image_compose":
      return {
        subtitle: `${cfg?.imageProvider ?? "runninghub"} · ${cfg?.imageEngine ?? "gpt-image-1.5/image-to-image/stable"} · ${cfg?.imageAspectRatio ?? "9:16"}`,
        badge: "image-to-image",
        badgeTone: "accent",
      };
    case "publish":
      return {
        subtitle: cfg?.publishEnabled
          ? (cfg.publishPlatforms ?? []).join(" · ") || "No platform selected"
          : "Disabled until account connections are configured",
        badge: cfg?.publishEnabled ? "enabled" : "off",
        badgeTone: cfg?.publishEnabled ? "accent" : "neutral",
      };
    case "prompt":
      return {
        subtitle: cfg?.promptText
          ? `${cfg.promptText.slice(0, 76)}${cfg.promptText.length > 76 ? "…" : ""}`
          : "Compose render instructions and reference-asset rules",
        badge: "Prompt",
        badgeTone: "accent",
      };
    case "aigc":
      return {
        subtitle: cfg?.videoEngine
          ? `${cfg.videoProvider ?? "video"} · ${cfg.videoEngine}`
          : "Select the video generation provider and model",
        badge: cfg?.videoProvider ?? "engine",
        badgeTone: cfg?.videoProvider === "seedance" ? "accent" : "neutral",
      };
    case "infinitetalk":
      return {
        subtitle: `${cfg?.videoEngine ?? "runpod/infinitetalk"} · photo + TTS audio + prompt → MP4`,
        badge: cfg?.videoProvider ?? "RunPod Hub",
        badgeTone: "accent",
      };
    case "chroma":
      return {
        subtitle: template.provider.type === "infinitetalk"
          ? `Person cutout + background photo → MP4 · key ${cfg?.keyColor ?? "#00B140"}`
          : cfg?.keyColor
            ? `Key ${cfg.keyColor} · ${cfg.deliverables?.length ?? 0} deliverable(s)`
            : "Matte the presenter, hand back a keyable background",
        badge: template.provider.type === "infinitetalk" ? "wired" : "not wired",
        badgeTone: template.provider.type === "infinitetalk" ? "success" : "warning",
      };
    case "render":
      return {
        subtitle:
          template.provider.endpoint ?? "Queue → webhook → sweep fallback",
        badge:
          template.provider.type +
          (template.provider.engine ? ` · ${template.provider.engine}` : ""),
        badgeTone: template.provider.type === "heygen" ? "accent" : "warning",
      };
    default:
      return {
        subtitle: "MP4 + thumbnail archived, cash refunded on failure",
        badge: stats.failed
          ? `${stats.succeeded} ready · ${stats.failed} failed`
          : `${stats.succeeded} ready`,
        badgeTone: stats.failed ? "danger" : "success",
      };
  }
}

function InnerCanvas({
  template,
  stats,
  onGraphChange,
  onConfigure,
}: {
  template: CanvasTemplate;
  stats: CanvasStats;
  onGraphChange: (nodes: CanvasNodeSpec[], edges: GraphEdge[]) => void;
  onConfigure: (key: string) => void;
}) {
  const { screenToFlowPosition, fitView } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const specs = useMemo(
    () => template.canvas?.nodes ?? DEFAULT_LAYOUT,
    [template.canvas],
  );
  const specsKey = JSON.stringify(specs);
  const storedEdges = template.canvas?.edges;
  const storedEdgesKey = JSON.stringify(storedEdges ?? null);
  const displayKey = JSON.stringify({
    tone: template.tone,
    provider: template.provider,
    avatarIds: template.avatarIds,
    voiceIds: template.voiceIds,
    moduleConfig: template.moduleConfig,
    stats,
  });

  const toGraphEdges = (list: Edge[]): GraphEdge[] =>
    list.map((e) => ({
      from: e.source,
      to: e.target,
      ...(e.targetHandle ? { targetInput: e.targetHandle } : {}),
    }));

  const emit = useCallback(
    (nodeList: Node<NodeData>[], edgeList: Edge[]) => {
      onGraphChange(
        nodeList.map((n) => ({
          key: n.data.moduleKey,
          x: Math.round(n.position.x),
          y: Math.round(n.position.y),
        })),
        toGraphEdges(edgeList),
      );
    },
    [onGraphChange],
  );

  const removeNode = useCallback(
    (key: string) => {
      setNodes((curNodes) => {
        const nextNodes = curNodes.filter((n) => n.data.moduleKey !== key);
        // 连着被删节点的线一起清掉，否则会留下悬空的边
        setEdges((curEdges) => {
          const nextEdges = curEdges.filter(
            (e) => e.source !== key && e.target !== key,
          );
          emit(nextNodes, nextEdges);
          return nextEdges;
        });
        return nextNodes;
      });
    },
    [setNodes, setEdges, emit],
  );

  // 只在切换模板或服务端布局真的变化时重建节点位置。
  // Job stats 每 3 秒刷新；若在那次刷新中重建位置，会把管理员正在拖动的节点
  // 瞬间拉回数据库中的旧坐标，表现为跳动或拖不走。
  useEffect(() => {
    setNodes(
      specs
        .filter((s) => MODULE_META[s.key])
        .map((s) => ({
          id: s.key,
          type: "canvas",
          position: { x: s.x, y: s.y },
          data: {
            moduleKey: s.key,
            ...describeModule(s.key, template, stats),
            onConfigure,
            onRemove: removeNode,
          },
        })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, specsKey]);

  // 配置、计数和状态变化只更新节点展示数据，绝不碰当前位置。
  useEffect(() => {
    setNodes((current) =>
      current.map((node) => ({
        ...node,
        data: {
          ...node.data,
          ...describeModule(node.data.moduleKey, template, stats),
          onConfigure,
          onRemove: removeNode,
        },
      })),
    );
    // template/stats are represented by displayKey to avoid a render loop from
    // the page's freshly-created stats object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayKey, onConfigure, removeNode, setNodes]);

  const edgeStyle = useMemo(() => {
    const flowing = stats.queued + stats.active > 0;
    return {
      animated: flowing,
      style: {
        stroke: flowing ? "var(--accent)" : "#ffffff2e",
        strokeWidth: 1.5,
      },
    };
  }, [stats.queued, stats.active]);

  // 老模板没存过 edges，按原来的固定接线还原一次。运行状态变化不重建线。
  useEffect(() => {
    const keys = specs.filter((sp) => MODULE_META[sp.key]).map((sp) => sp.key);
    const stored = template.canvas?.edges ?? defaultEdges(keys);
    setEdges(
      stored
        .filter((e) => keys.includes(e.from) && keys.includes(e.to))
        .map((e) => {
          const targetInput =
            e.targetInput ?? inferTargetInput(e.from, e.to);
          return {
            id: `e-${e.from}-${e.to}-${targetInput ?? "default"}`,
            source: e.from,
            target: e.to,
            targetHandle: targetInput,
          };
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, specsKey, storedEdgesKey]);

  // Each template can have a very different graph width. Re-center only when
  // switching templates; doing this during stats refreshes would fight manual
  // panning and node dragging.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      void fitView({ padding: 0.15, duration: 180 });
    });
    return () => cancelAnimationFrame(frame);
  }, [template.id, fitView]);

  // 流动动画可以随任务状态更新，但保留当前连线集合。
  useEffect(() => {
    setEdges((current) =>
      current.map((edge) => ({ ...edge, ...edgeStyle })),
    );
  }, [edgeStyle, setEdges]);

  const isValidConnection = useCallback(
    (c: Connection | Edge) =>
      Boolean(
        c.source &&
          c.target &&
          isEdgeAllowed(c.source, c.target, c.targetHandle),
      ),
    [],
  );

  const onConnect = useCallback(
    (c: Connection) => {
      setEdges((cur) => {
        if (
          cur.some(
            (e) =>
              e.source === c.source &&
              e.target === c.target &&
              e.targetHandle === c.targetHandle,
          )
        )
          return cur;
        const next = addEdge({ ...c, ...edgeStyle }, cur);
        setNodes((curNodes) => (emit(curNodes, next), curNodes));
        return next;
      });
    },
    [setEdges, setNodes, emit, edgeStyle],
  );

  /** 点线即删 —— 比选中再按 Delete 少一步，且不和画布快捷键打架 */
  const onEdgeClick = useCallback(
    (_: unknown, edge: Edge) => {
      setEdges((cur) => {
        const next = cur.filter((e) => e.id !== edge.id);
        setNodes((curNodes) => (emit(curNodes, next), curNodes));
        return next;
      });
    },
    [setEdges, setNodes, emit],
  );

  const problems = useMemo(
    () =>
      validateGraph(
        nodes.map((n) => n.data.moduleKey),
        toGraphEdges(edges),
      ),
    [nodes, edges],
  );

  return (
    <>
      {problems.length > 0 && (
        <div className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3.5 py-2.5 text-[12px] leading-5 text-amber-200/90">
          <span className="font-medium">
            This graph won&apos;t run as drawn:
          </span>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      <div
        className="h-[440px] w-full overflow-hidden rounded-2xl border border-line bg-[#0b0b12]"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(DND_MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
          }
        }}
        onDrop={(e) => {
          const key = e.dataTransfer.getData(DND_MIME);
          if (!MODULE_META[key]) return;
          e.preventDefault();
          setNodes((cur) => {
            if (cur.some((n) => n.data.moduleKey === key)) return cur;
            const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
            const next = [
              ...cur,
              {
                id: key,
                type: "canvas",
                position: { x: pos.x - 112, y: pos.y - 40 },
                data: {
                  moduleKey: key,
                  ...describeModule(key, template, stats),
                  onConfigure,
                  onRemove: removeNode,
                },
              } satisfies Node<NodeData>,
            ];
            setEdges((curEdges) => (emit(next, curEdges), curEdges));
            return next;
          });
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          isValidConnection={isValidConnection}
          onNodeDragStop={() =>
            setNodes((cur) => {
              setEdges((curEdges) => (emit(cur, curEdges), curEdges));
              return cur;
            })
          }
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.4}
          maxZoom={1.5}
          deleteKeyCode={null}
          colorMode="dark"
        >
          <Background
            variant={BackgroundVariant.Dots}
            gap={22}
            size={1.5}
            color="#ffffff14"
          />
        </ReactFlow>
      </div>
      <p className="mt-2 text-[11px] text-muted">
        Drag from a node&apos;s right dot to another node&apos;s left dot to
        wire it up; click a line to remove it. The render pipeline follows this
        graph.
      </p>
    </>
  );
}

export function WorkflowCanvas(props: {
  template: CanvasTemplate;
  stats: CanvasStats;
  onGraphChange: (nodes: CanvasNodeSpec[], edges: GraphEdge[]) => void;
  onConfigure: (key: string) => void;
}) {
  return (
    <ReactFlowProvider>
      <InnerCanvas {...props} />
    </ReactFlowProvider>
  );
}
